// src/pages/accounting/ProjectCostLedgerPage.jsx
// ─────────────────────────────────────────────────────────────────
// Project Cost Ledger — Auditable monthly ledger of project cost
//
// Project cost = Total labour cost + Project-specific expenses
//
// Labour cost per user-month:
//   monthly_total_salary_cost_with_markup = (base_salary + bonus) × markup_factor
//   normalized_total = max(total_logged_hours, contracted_hours)
//   %worked = project_hours / normalized_total
//   allocated_cost = monthly_total_salary_cost_with_markup × %worked
//
// Expenses:
//   Actual: real expenses logged in selected period (sum)
//   Smoothed: yearly totals by category / 12, summed across selected months
//
// Period selectors: Month, Calendar Year, Indian FY, Total project, Custom range
// All calculations are monthly underneath; multi-month → aggregate.
// ─────────────────────────────────────────────────────────────────

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { ChevronRight, ChevronDown, Info } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioMonthPicker,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { validateSalaryConservation } from '../../utils/validateSalaryConservation';
import { computeProjectLabour, computeInternalCostRedistribution } from '../../utils/projectLabour';
import { computeActivityOverheadEngine, getProjectMonths as computeProjectMonths } from '../../utils/activityOverheadEngine';
const PRESENTATION_CODE = '1b';

// ════════════════════════════════════════════════════════════════
// MONTH UTILITIES
// ════════════════════════════════════════════════════════════════

/** YYYY-MM string from year+month ints */
const ym = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

/** Parse YYYY-MM or YYYY-MM-DD → { year, month } */
const parseYM = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{1,2})/);
  return m ? { year: parseInt(m[1]), month: parseInt(m[2]) } : null;
};

/** Generate array of YYYY-MM strings for a range (inclusive) */
function monthRange(startYM, endYM) {
  const s = parseYM(startYM);
  const e = parseYM(endYM);
  if (!s || !e) return [];
  const months = [];
  let y = s.year, m = s.month;
  while (y < e.year || (y === e.year && m <= e.month)) {
    months.push(ym(y, m));
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

/** Today as YYYY-MM */
const todayYM = () => {
  const d = new Date();
  return ym(d.getFullYear(), d.getMonth() + 1);
};

/** Short month label: "Jan 25" */
const shortMonth = (ymStr) => {
  const p = parseYM(ymStr);
  if (!p) return ymStr;
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[p.month - 1]} ${String(p.year).slice(2)}`;
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ════════════════════════════════════════════════════════════════
// EXPENSE CATEGORIES (stable)
// ════════════════════════════════════════════════════════════════
const EXPENSE_CATEGORIES = ['Consultants', 'Travel', 'Materials', 'Miscellaneous'];

/** Normalize expense category into one of the four standard buckets */
function normalizeExpenseCategory(cat) {
  if (!cat) return 'Miscellaneous';
  const lower = cat.toLowerCase();
  if (lower.includes('consult')) return 'Consultants';
  if (lower.includes('travel')) return 'Travel';
  if (lower.includes('material') || lower.includes('print') || lower.includes('model') || lower.includes('render')) return 'Materials';
  return 'Miscellaneous';
}

// ════════════════════════════════════════════════════════════════
// ID NORMALIZATION
// ════════════════════════════════════════════════════════════════
// JS object keys are always strings. Time entries indexed by userId
// become string keys ("123"), but App.js helper functions
// (getLedgerMonthlyComp, getInternalCostRate, getUserProfileAtDate)
// use strict === against activeUsers[].id which is numeric (via
// normalizeId in useSupabase). String "123" === Number 123 → false.
// This helper ensures we pass numeric IDs to App.js functions.
// ════════════════════════════════════════════════════════════════
const toNumericId = (id) => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && /^-?\d+$/.test(id.trim())) return parseInt(id, 10);
  return id;
};

// ════════════════════════════════════════════════════════════════
// AUTH USER ID RESOLUTION
// ════════════════════════════════════════════════════════════════
// The salary ledger (fullSalaryLedger) is keyed by auth_user_id (UUID),
// NOT by the numeric users.id. Different code paths in the app may store
// the auth UUID under different property names. This helper resolves
// whichever field is available on the user object.
// ════════════════════════════════════════════════════════════════
const resolveAuthId = (u) =>
  u.auth_user_id ||
  u.authUserId ||
  u.authId ||
  u.authUUID ||
  u.user_uuid ||
  u.userUuid ||
  null;


// ════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════

export default function ProjectCostLedgerPage({
  activeProjects,
  activeClients,
  activeUsers,
  activeTimeEntries,
  activeExpenses,
  activeOfficeOverheads = [],
  monthlyOfficeOverheads = [],
  monthlyOfficeOverheadsLoading = false,
  calcMonthlyCost,
  isEmployerBurdenCategory,
  getUserProfileAtDate,
  getEffectiveContractedHours,
  computeOverheadAllocation,
  fmtCurr,
  currSymbol,
  settingsHook,
  isAdmin,
  projectStatusFilter,
  setProjectStatusFilter,
}) {
  // Essential profit % target from settings
  const essentialProfitPct = settingsHook?.essentialProfitPercentage ?? 20;

  // Dormancy pool fraction (from app_settings, default 3%)
  const DORMANCY_POOL_PCT = (settingsHook?.dormancyPoolPct ?? 3) / 100;
  // ──────────────────────────────────────────────────────
  // STATE: Period selection
  // ──────────────────────────────────────────────────────
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [periodType, setPeriodType] = useState('total');          // month | indianFY | calendarYear | total | custom
  const [selYear, setSelYear] = useState(currentYear);
  const [selMonth, setSelMonth] = useState(currentMonth);
  const [fyYear, setFyYear] = useState(currentYear);             // FY that starts in April of fyYear
  const [cyYear, setCyYear] = useState(currentYear);             // Calendar year (Jan–Dec)
  const [customStart, setCustomStart] = useState(ym(currentYear, 1));
  const [customEnd, setCustomEnd] = useState(ym(currentYear, currentMonth));
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedVariance, setExpandedVariance] = useState({});
  const [showIncompleteTimesheets, setShowIncompleteTimesheets] = useState(false);
  const [showMissingSalary, setShowMissingSalary] = useState(false);
  const [showMonthGrid, setShowMonthGrid] = useState({});         // per-project

  // Project status filter — driven by App.js global state.
  const statusFilter = projectStatusFilter ?? 'all';

  // Dev diagnostics
  if (process.env.NODE_ENV !== 'production') {
    console.log('[ProjectCostLedger]', {
      totalProjects: activeProjects?.length,
      totalTimeEntries: activeTimeEntries?.length,
      totalExpenses: activeExpenses?.length,
      sampleProject: activeProjects?.[0]?.projectCode || activeProjects?.[0]?.name,
    });
  }

  // ──────────────────────────────────────────────────────
  // DERIVED: Selected period months
  // ──────────────────────────────────────────────────────
  const selectedMonths = useMemo(() => {
    switch (periodType) {
      case 'month':
        return [ym(selYear, selMonth)];
      case 'indianFY':
        return monthRange(ym(fyYear, 4), ym(fyYear + 1, 3));
      case 'calendarYear':
        return monthRange(ym(cyYear, 1), ym(cyYear, 12));
      case 'custom':
        return monthRange(customStart, customEnd);
      case 'total':
        return []; // per-project, computed below
      default:
        return [ym(selYear, selMonth)];
    }
  }, [periodType, selYear, selMonth, fyYear, cyYear, customStart, customEnd]);

  const periodLabel = useMemo(() => {
    switch (periodType) {
      case 'month': return `${MONTH_NAMES[selMonth - 1]} ${selYear}`;
      case 'indianFY': return `FY ${fyYear}–${String(fyYear + 1).slice(2)}`;
      case 'calendarYear': return `${cyYear}`;
      case 'custom': return `${shortMonth(customStart)} – ${shortMonth(customEnd)}`;
      case 'total': return 'Total Project Duration';
      default: return '';
    }
  }, [periodType, selYear, selMonth, fyYear, cyYear, customStart, customEnd]);

  // ──────────────────────────────────────────────────────
  // INDEX: Time entries by (projectId, userId, yearMonth)
  // ──────────────────────────────────────────────────────
  const { byProject, byUserMonth } = useMemo(() => {
    // byProject[projectId][userId][yearMonth] = hours
    // byUserMonth[userId][yearMonth] = totalHours (all projects)
    const bp = {};
    const bum = {};

    activeTimeEntries.forEach(e => {
      const ymKey = e.date.substring(0, 7); // YYYY-MM
      const hours = (e.duration || 0) / 3600;
      const pid = e.projectId;
      const uid = e.userId;

      // Per project/user/month
      if (!bp[pid]) bp[pid] = {};
      if (!bp[pid][uid]) bp[pid][uid] = {};
      bp[pid][uid][ymKey] = (bp[pid][uid][ymKey] || 0) + hours;

      // Per user/month (all projects)
      if (!bum[uid]) bum[uid] = {};
      bum[uid][ymKey] = (bum[uid][ymKey] || 0) + hours;
    });

    return { byProject: bp, byUserMonth: bum };
  }, [activeTimeEntries]);

  // ──────────────────────────────────────────────────────
  // EXPENSE PAYMENTS: Client reimbursements (bank_payment_events)
  // ──────────────────────────────────────────────────────
  const [clientPayments, setClientPayments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('bank_payment_events')
      .select('amount, txn_date, linked_project_id')
      .eq('expense_payment', true)
      .eq('status', 'expense')
      .not('linked_project_id', 'is', null)
      .then(({ data }) => {
        if (!cancelled && data) {
          setClientPayments(data.map(r => ({
            amount: Number(r.amount) || 0,
            txnDate: r.txn_date ?? null,
            linkedProjectId: r.linked_project_id ? Number(r.linked_project_id) : null,
          })));
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Index: client deduction by (projectId, yearMonth) in INR
  const clientDeductionIndex = useMemo(() => {
    const idx = {};
    clientPayments.forEach(p => {
      if (!p.linkedProjectId || !p.txnDate) return;
      const ymKey = p.txnDate.substring(0, 7);
      const pid = p.linkedProjectId;
      if (!idx[pid]) idx[pid] = {};
      idx[pid][ymKey] = (idx[pid][ymKey] || 0) + p.amount;
    });
    return idx;
  }, [clientPayments]);

  // ──────────────────────────────────────────────────────
  // INDEX: Expenses by (projectId, yearMonth, category)
  // ──────────────────────────────────────────────────────
  const expenseIndex = useMemo(() => {
    // expIdx[projectId][yearMonth][category] = amount
    // expByYear[projectId][year][category] = yearlyTotal
    const expIdx = {};
    const expByYear = {};

    activeExpenses.forEach(e => {
      if (e.status === 'planned') return; // Don't count planned
      const pid = e.projectId;
      const date = e.date || '';
      const ymKey = date.substring(0, 7);
      const yearKey = date.substring(0, 4);
      const cat = normalizeExpenseCategory(e.category);
      const amt = e.amount || 0;

      if (!expIdx[pid]) expIdx[pid] = {};
      if (!expIdx[pid][ymKey]) expIdx[pid][ymKey] = {};
      expIdx[pid][ymKey][cat] = (expIdx[pid][ymKey][cat] || 0) + amt;

      if (!expByYear[pid]) expByYear[pid] = {};
      if (!expByYear[pid][yearKey]) expByYear[pid][yearKey] = {};
      expByYear[pid][yearKey][cat] = (expByYear[pid][yearKey][cat] || 0) + amt;
    });

    return { expIdx, expByYear };
  }, [activeExpenses]);

  // ──────────────────────────────────────────────────────
  // HELPER: Get months for a specific project (total mode)
  // ──────────────────────────────────────────────────────
  // Shared with Project Profitability via src/utils/activityOverheadEngine.js
  // (the Profitability page passes incomeByProjectMonth too; Cost Ledger
  // doesn't compute paid invoices the same way and relies on the unified
  // helper's project-metadata + extend-to-today behaviour).
  const getProjectMonths = useCallback((project) => computeProjectMonths(project, {
    byProject, periodType, selectedMonths,
  }), [periodType, selectedMonths, byProject]);

  // ──────────────────────────────────────────────────────
  // HELPER: Get monthly true cash labour cost for a user-month.
  // Delegates to centralised calcMonthlyTrueCost (App.js) which returns
  // base + bonus + pro-rata employer burden (NL Pension + Salary Tax)
  // with markup=1. No markup is applied on this page; markup is a
  // target-revenue concept that belongs on the Salary Cost report.
  // ──────────────────────────────────────────────────────
  const getMonthlyCostWithMarkup = useCallback((userId, yearMonth) => {
    const numId = toNumericId(userId);
    const p = parseYM(yearMonth);
    if (!p) return { monthlyComp: 0, markup: 0, totalCost: 0, source: 'none' };
    const cost = calcMonthlyCost(numId, p.year, p.month);
    return {
      monthlyComp: cost.baseSalary + cost.bonus,
      markup: cost.markup,
      totalCost: cost.totalCost,
      source: cost.source,
      baseSalary: cost.baseSalary,
      bonus: cost.bonus,
    };
  }, [calcMonthlyCost]);

  // ──────────────────────────────────────────────────────
  // CORE: Compute per-project data for a set of months
  // ──────────────────────────────────────────────────────
  const computeProjectData = useCallback((project, months) => {
    if (!months || months.length === 0) {
      return { userRows: [], expenses: { actual: {}, smoothed: {} }, totals: { labour: 0, actualExp: 0, smoothedExp: 0 } };
    }

    // ── LABOUR via shared engine ─────────────────────────────────────
    // Single source of truth — Project Profitability calls the same
    // utility with the same inputs and therefore reaches the same totals.
    const labourBreakdown = computeProjectLabour({
      byProject, byUserMonth, projectId: project.id, months,
      getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost,
    });
    const totalLabourCost = labourBreakdown.totalAllocated;

    // Decorate the per-user breakdown with Cost Ledger-specific extras
    // (variance diagnostics, predominant source, display fields) without
    // recomputing labour.
    const userRows = [];
    Object.entries(labourBreakdown.byUser).forEach(([uid, ub]) => {
      const user = activeUsers.find(u => String(u.id) === String(uid));
      if (!user) return;
      const sourceCounts = {};
      const userMonthData = [];
      const varianceDiag = [];

      ub.months.forEach(m => {
        const cost = m.costData || {};
        sourceCounts[cost.source] = (sourceCounts[cost.source] || 0) + 1;

        if (process.env.NODE_ENV !== 'production') {
          const totalAllocated = m.normalizedTotal > 0
            ? m.totalLogged * (m.monthlyCost / m.normalizedTotal)
            : 0;
          validateSalaryConservation(uid, m.ymKey, totalAllocated, m.monthlyCost, m.totalLogged, m.contractedHours);
        }

        userMonthData.push({
          month: m.ymKey,
          projectHours: m.projectHours,
          totalLogged: m.totalLogged,
          contractedHours: m.contractedHours,
          normalizedTotal: m.normalizedTotal,
          pctWorked: m.pctWorked,
          monthlyCost: m.monthlyCost,
          allocatedCost: m.allocatedCost,
          source: cost.source,
          baseSalary: cost.baseSalary || 0,
          bonus: cost.bonus || 0,
          markup: cost.markup,
          authUserIdResolved: cost.authUserIdResolved,
          ledgerRowFound: cost.ledgerRowFound,
        });

        const theoreticalHourlyCost = (cost.totalCost || 0) / 176;
        const recordedHoursCost = m.totalLogged * theoreticalHourlyCost;
        const variance = recordedHoursCost - m.monthlyCost;
        const toleranceHours = m.contractedHours * 0.10;
        const toleranceCost = toleranceHours * theoreticalHourlyCost;
        const excessVariance = Math.max(0, variance - toleranceCost);
        varianceDiag.push({
          month: m.ymKey,
          recordedHours: m.totalLogged,
          contractedHours: m.contractedHours,
          theoreticalHourlyCost,
          fixedMonthlySalaryCost: cost.totalCost || 0,
          variance,
          toleranceBand: toleranceCost,
          excessVariance,
          withinTolerance: excessVariance === 0,
        });
      });

      // Aggregated % worked across the full month window (includes empty
      // months for the denominator so the percentage reflects engagement
      // over the whole period).
      const aggNormalizedTotal = months.reduce((sum, ymKey) => {
        const totalLogged = byUserMonth[uid]?.[ymKey] || 0;
        const p = parseYM(ymKey);
        const ch = (getEffectiveContractedHours && p)
          ? getEffectiveContractedHours(ub.numUid, p.year, p.month)
          : (getUserProfileAtDate(ub.numUid, p ? `${p.year}-${String(p.month).padStart(2, '0')}-15` : '').contractedHours || 176);
        return sum + Math.max(totalLogged, ch);
      }, 0);
      const aggPctWorked = aggNormalizedTotal > 0 ? ub.projectHours / aggNormalizedTotal : 0;

      const primarySource = Object.entries(sourceCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'missing';

      userRows.push({
        userId: uid,
        userName: user.name || `User ${uid}`,
        userInitials: (user.name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2),
        months: userMonthData,
        varianceDiag,
        primarySource,
        totals: {
          projectHours: ub.projectHours,
          totalLogged: ub.totalLogged,
          avgContractedHours: ub.months.length > 0 ? ub.contractedHours / ub.months.length : 176,
          pctWorked: aggPctWorked,
          allocatedCost: ub.allocatedCost,
        },
      });
    });

    // Sort by allocated cost descending
    userRows.sort((a, b) => b.totals.allocatedCost - a.totals.allocatedCost);

    // ── EXPENSES: Actual + Smoothed ──
    const actualByCat = {};
    const smoothedByCat = {};

    EXPENSE_CATEGORIES.forEach(cat => {
      actualByCat[cat] = 0;
      smoothedByCat[cat] = 0;
    });

    // Actual: sum expenses in selected months
    months.forEach(ymKey => {
      EXPENSE_CATEGORIES.forEach(cat => {
        actualByCat[cat] += expenseIndex.expIdx[project.id]?.[ymKey]?.[cat] || 0;
      });
    });

    // Smoothed: for each year in the period, get yearly total / 12 × months in that year
    const yearMonthCounts = {};
    months.forEach(ymKey => {
      const yr = ymKey.substring(0, 4);
      yearMonthCounts[yr] = (yearMonthCounts[yr] || 0) + 1;
    });

    Object.entries(yearMonthCounts).forEach(([yr, count]) => {
      EXPENSE_CATEGORIES.forEach(cat => {
        const yearlyTotal = expenseIndex.expByYear[project.id]?.[yr]?.[cat] || 0;
        smoothedByCat[cat] += (yearlyTotal / 12) * count;
      });
    });

    const grossActualExp = Object.values(actualByCat).reduce((a, b) => a + b, 0);
    // Subtract client reimbursements received in selected months (INR, matches expense amounts)
    const clientReimbursed = months.reduce((s, ymKey) => s + (clientDeductionIndex[project.id]?.[ymKey] || 0), 0);
    const totalActualExp = Math.max(0, grossActualExp - clientReimbursed);
    const totalSmoothedExp = Object.values(smoothedByCat).reduce((a, b) => a + b, 0);

    return {
      userRows,
      expenses: { actual: actualByCat, smoothed: smoothedByCat },
      totals: {
        labour: totalLabourCost,
        actualExp: totalActualExp,
        smoothedExp: totalSmoothedExp,
        totalActual: totalLabourCost + totalActualExp,
        totalSmoothed: totalLabourCost + totalSmoothedExp,
      },
    };
  }, [byProject, byUserMonth, activeUsers, getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost, expenseIndex, clientDeductionIndex]);

  // ──────────────────────────────────────────────────────
  // FILTERED PROJECTS: by status
  // ──────────────────────────────────────────────────────
  const filteredProjects = useMemo(() => {
    if (statusFilter === 'all' || statusFilter === 'default') return activeProjects;
    return activeProjects.filter(p => p.status === statusFilter);
  }, [activeProjects, statusFilter]);

  // ──────────────────────────────────────────────────────
  // COMPUTE: All project data for current period
  // ──────────────────────────────────────────────────────
  const projectDataMap = useMemo(() => {
    const map = {};
    filteredProjects.forEach(project => {
      const months = getProjectMonths(project);
      map[project.id] = {
        months,
        ...computeProjectData(project, months),
      };
    });
    return map;
  }, [filteredProjects, getProjectMonths, computeProjectData]);

  // ──────────────────────────────────────────────────────
  // SORTED PROJECTS: by total cost descending
  // ──────────────────────────────────────────────────────
  const sortedProjects = useMemo(() => {
    // Always show ALL filtered projects — never filter by cost.
    // Sort: projects with cost first (descending), then alphabetical.
    return [...filteredProjects].sort((a, b) => {
      const da = projectDataMap[a.id]?.totals?.totalActual || 0;
      const db = projectDataMap[b.id]?.totals?.totalActual || 0;
      if (da !== db) return db - da;
      const na = (a.projectCode || a.name || '').toLowerCase();
      const nb = (b.projectCode || b.name || '').toLowerCase();
      return na.localeCompare(nb);
    });
  }, [filteredProjects, projectDataMap]);

  // ──────────────────────────────────────────────────────
  // TOGGLE handlers
  // ──────────────────────────────────────────────────────
  const toggleProject = (pid) => {
    setExpandedProjects(prev => ({ ...prev, [pid]: !prev[pid] }));
  };
  const toggleVariance = (pid) => {
    setExpandedVariance(prev => ({ ...prev, [pid]: !prev[pid] }));
  };

  // ──────────────────────────────────────────────────────
  // GRAND TOTALS
  // ──────────────────────────────────────────────────────
  const grandTotals = useMemo(() => {
    let labour = 0, actualExp = 0, smoothedExp = 0;
    sortedProjects.forEach(p => {
      const d = projectDataMap[p.id];
      if (d) {
        labour += d.totals.labour;
        actualExp += d.totals.actualExp;
        smoothedExp += d.totals.smoothedExp;
      }
    });
    return {
      labour,
      actualExp,
      smoothedExp,
      totalActual: labour + actualExp,
      totalSmoothed: labour + smoothedExp,
    };
  }, [sortedProjects, projectDataMap]);

  // ──────────────────────────────────────────────────────
  // OVERHEAD ALLOCATION (management + support untracked costs)
  // ──────────────────────────────────────────────────────
  const overheadData = useMemo(() => {
    if (!computeOverheadAllocation) return null;
    // For 'total' mode, build a union of all project months
    let months = selectedMonths;
    if (periodType === 'total') {
      const allMonths = new Set();
      sortedProjects.forEach(p => {
        const pm = getProjectMonths(p);
        pm.forEach(m => allMonths.add(m));
      });
      months = [...allMonths].sort();
    }
    if (months.length === 0) return null;
    return computeOverheadAllocation(months);
  }, [computeOverheadAllocation, selectedMonths, periodType, sortedProjects, getProjectMonths]);

  // ──────────────────────────────────────────────────────
  // ACTIVITY-BASED OVERHEAD + DORMANCY (matches ProjectProfitabilityPage)
  // ──────────────────────────────────────────────────────
  // Employer-burden categories (Pension, Salary Tax) are excluded from the
  // overhead pool — they're attributed to per-user Direct Labour via
  // calcMonthlyTrueCost instead. Without this filter the same cash would be
  // counted once in Direct Labour and again in Op. Overhead.
  const isEmployerBurden = isEmployerBurdenCategory
    || ((category) => category === 'Pension' || category === 'Salary Tax');

  // Pre-index: monthly overhead rows by office+month
  const monthlyOHIndex = useMemo(() => {
    const idx = {};
    monthlyOfficeOverheads.forEach(o => {
      if (isEmployerBurden(o.category)) return;
      const loc = (o.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
      const key = `${loc}|${o.month}`;
      idx[key] = (idx[key] || 0) + (o.amountInr ?? 0);
    });
    return idx;
  }, [monthlyOfficeOverheads, isEmployerBurden]);

  // Pre-index: yearly overhead for fallback
  const yearlyOHIndex = useMemo(() => {
    const idx = {};
    activeOfficeOverheads.forEach(o => {
      if (isEmployerBurden(o.category)) return;
      const loc = (o.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
      const yr = o.year;
      if (!idx[loc]) idx[loc] = {};
      idx[loc][yr] = (idx[loc][yr] || 0) + (o.amountInInr ?? o.amount ?? 0);
    });
    return idx;
  }, [activeOfficeOverheads, isEmployerBurden]);

  // Pre-index: which projects have ANY time entries (evidence of life for dormancy)
  const projectsWithAnyTime = useMemo(() => {
    const s = new Set();
    activeTimeEntries.forEach(e => {
      if (e.projectId && (e.duration || 0) > 0) s.add(toNumericId(e.projectId));
    });
    return s;
  }, [activeTimeEntries]);

  // Internal projects (client = BRIO, or no client) — their labour is
  // pooled and redistributed to billable projects as Internal OH.
  const internalProjectIds = useMemo(() => {
    const clientMap = {};
    (activeClients || []).forEach(c => { clientMap[toNumericId(c.id)] = c; });
    const ids = new Set();
    (activeProjects || []).forEach(p => {
      const client = clientMap[toNumericId(p.clientId)];
      if (!client || !p.clientId || (client.name || '').toLowerCase() === 'brio') {
        ids.add(toNumericId(p.id));
      }
    });
    return ids;
  }, [activeProjects, activeClients]);

  // Activity-based overhead engine — shared with Project Profitability via
  // src/utils/activityOverheadEngine.js.
  const activityOverheadEngine = useMemo(
    () => computeActivityOverheadEngine({
      activeTimeEntries, activeUsers, activeProjects, internalProjectIds,
      monthlyOHIndex, yearlyOHIndex,
      monthlyOfficeOverheads, yearlyOfficeOverheads: activeOfficeOverheads,
      DORMANCY_POOL_PCT,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTimeEntries, activeUsers, activeProjects, activeOfficeOverheads,
     monthlyOfficeOverheads, monthlyOHIndex, yearlyOHIndex, projectsWithAnyTime,
     internalProjectIds, DORMANCY_POOL_PCT]
  );

  // Internal OH per (project, month) — labour from internal projects
  // (BRIO General etc.) redistributed to billable projects by hour share.
  // Same shared helper Profitability uses, so totals cannot drift.
  const internalCostByProjectMonth = useMemo(() => computeInternalCostRedistribution({
    byProject,
    byUserMonth,
    activeUsers,
    internalProjectIds,
    billableOfficeHours: activityOverheadEngine.billableOfficeHours,
    projOfficeHours: activityOverheadEngine.projOfficeHours,
    getUserProfileAtDate,
    getEffectiveContractedHours,
    calcMonthlyCost,
  }), [internalProjectIds, activityOverheadEngine, byProject, byUserMonth,
       activeUsers, getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost]);

  // ──────────────────────────────────────────────────────
  // YEAR OPTIONS (for selectors)
  // ──────────────────────────────────────────────────────
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = 2018; y <= currentYear + 1; y++) years.push(y);
    return years;
  }, [currentYear]);

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════

  const code = PRESENTATION_CODE;
  const tableProps = getTablePropsFromCode(code);

  return (
    <div className="brio-analytics-font" style={{ maxWidth: 960, margin: '0 auto' }}>
      <BrioFrame>
        <div style={{ paddingTop: 'var(--brio-space-m)', paddingBottom: 'var(--brio-space-m)' }}>

        {/* ── Title + Period selector ── */}
        <BrioAnalyticsSurface padding="m">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <h3 className="brio-section-header__title" style={{ margin: 0 }}>
              project cost ledger
            </h3>
            <div style={{ fontSize: 12, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>
              {periodLabel} · {sortedProjects.length} project{sortedProjects.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Period type selector */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { id: 'month', label: 'Month' },
              { id: 'calendarYear', label: 'Calendar Year' },
              { id: 'indianFY', label: 'Indian FY' },
              { id: 'total', label: 'Total Duration' },
              { id: 'custom', label: 'Custom Range' },
            ].map(pt => (
              <button
                key={pt.id}
                onClick={() => setPeriodType(pt.id)}
                className={`brio-btn ${periodType === pt.id ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                {pt.label}
              </button>
            ))}

          </div>

          {/* Period-specific selectors */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {periodType === 'month' && (
              <>
                <select
                  value={selMonth}
                  onChange={e => setSelMonth(parseInt(e.target.value))}
                  className="brio-modal-select"
                  style={{ width: 'auto', fontSize: 12 }}
                >
                  {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <select
                  value={selYear}
                  onChange={e => setSelYear(parseInt(e.target.value))}
                  className="brio-modal-select"
                  style={{ width: 'auto', fontSize: 12 }}
                >
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </>
            )}

            {periodType === 'calendarYear' && (
              <select
                value={cyYear}
                onChange={e => setCyYear(parseInt(e.target.value))}
                className="brio-modal-select"
                style={{ width: 'auto', fontSize: 12 }}
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}

            {periodType === 'indianFY' && (
              <select
                value={fyYear}
                onChange={e => setFyYear(parseInt(e.target.value))}
                className="brio-modal-select"
                style={{ width: 'auto', fontSize: 12 }}
              >
                {yearOptions.map(y => <option key={y} value={y}>FY {y}–{String(y + 1).slice(2)}</option>)}
              </select>
            )}

            {periodType === 'custom' && (
              <>
                <label style={{ fontSize: 11, color: 'var(--brio-text-secondary)' }}>From:</label>
                <BrioMonthPicker
                  value={customStart}
                  onChange={v => setCustomStart(v)}
                  style={{ width: 'auto', fontSize: 12 }}
                />
                <label style={{ fontSize: 11, color: 'var(--brio-text-secondary)' }}>To:</label>
                <BrioMonthPicker
                  value={customEnd}
                  onChange={v => setCustomEnd(v)}
                  style={{ width: 'auto', fontSize: 12 }}
                />
              </>
            )}

            {periodType === 'total' && (
              <span style={{ fontSize: 11, color: 'var(--brio-text-secondary)', fontStyle: 'italic' }}>
                Each project uses its own start → end (or today) date range
              </span>
            )}
          </div>
        </BrioAnalyticsSurface>

        {/* ── Grand totals bar ── */}
        <BrioAnalyticsSurface padding="m" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Labour Cost</div>
              <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(grandTotals.labour)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expenses (Actual)</div>
              <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(grandTotals.actualExp)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expenses (Smoothed)</div>
              <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(grandTotals.smoothedExp)}</div>
            </div>
            <div style={{ borderLeft: '1px solid var(--brio-border)', paddingLeft: 20 }}>
              <div
                style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'help' }}
                title={'Direct Cost = labour cost on the project (base + bonus + pro-rata employer burden for NL employees, with markup = 1) plus reimbursable project expenses. Employer burden (Pension + Salary Tax) is included here, not in Op. Overhead, to match the Project Profitability methodology.'}
              >
                Direct Cost
              </div>
              <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(grandTotals.totalActual)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Cost</div>
              <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--brio-gold-hover)', fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(grandTotals.totalActual * (1 + essentialProfitPct / 100))}</div>
            </div>
          </div>

          {/* Activity-based overhead summary */}
          {(() => {
            const totalOp = Object.values(activityOverheadEngine.totalByProject).reduce((s, v) => s + v, 0);
            const totalDor = Object.values(activityOverheadEngine.dormancyTotalByProject).reduce((s, v) => s + v, 0);
            if (totalOp <= 0 && totalDor <= 0) return null;
            return (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--brio-border)', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <div>
                <div
                  style={{ fontSize: 10, color: '#7c6f1a', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'help' }}
                  title={'Op. Overhead = office expenses (rent, software, utilities, etc.) from office_overheads_yearly plus imported office expenses, allocated to this project by its share of total logged hours per office-month. Employer burden (Pension + Salary Tax) is EXCLUDED from this pool — those costs flow through Direct Cost via the NL employees who incur them.'}
                >
                  Op. Overhead
                </div>
                <div style={{ fontSize: 13, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(totalOp)}</div>
              </div>
              {totalDor > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: '#8b6914', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dormancy Cost</div>
                  <div style={{ fontSize: 13, fontWeight: 300, fontVariantNumeric: 'tabular-nums', color: '#8b6914' }}>{fmtCurr(totalDor)}</div>
                </div>
              )}
              <div style={{ borderLeft: '1px solid var(--brio-border)', paddingLeft: 20 }}>
                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Overhead</div>
                <div style={{ fontSize: 13, fontWeight: 400, fontVariantNumeric: 'tabular-nums', color: '#7c6f1a' }}>{fmtCurr(totalOp + totalDor)}</div>
              </div>
            </div>
            );
          })()}
        </BrioAnalyticsSurface>

        {/* ── Missing salary data warning ── */}
        {overheadData && overheadData.missingSalaryUsers && overheadData.missingSalaryUsers.length > 0 && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 8, borderLeft: '3px solid #E65100' }}>
            <div
              onClick={() => setShowMissingSalary(prev => !prev)}
              style={{ fontSize: 11, fontWeight: 400, color: '#E65100', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}
            >
              <span style={{ fontSize: 8, transition: 'transform 0.15s', transform: showMissingSalary ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              Missing Salary Data — {overheadData.missingSalaryUsers.length} users with ₹0 cost ({overheadData.missingSalaryUsers.filter(u => u.office === 'NL').length} NL, {overheadData.missingSalaryUsers.filter(u => u.office === 'IN').length} IN)
            </div>
            {showMissingSalary && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
                  These users have salary data missing for most of their employed months. Their labour cost is
                  calculated as ₹0, which understates project costs and overhead pools. Check the salary ledger,
                  salary matrix, and role history (nl_schaal/nl_trede for NL users) for each user.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {overheadData.missingSalaryUsers.map(u => (
                    <div key={u.userId} style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-text-secondary)', background: 'rgba(230, 81, 0, 0.06)', padding: '4px 8px', borderRadius: 4 }}>
                      <span style={{ fontWeight: 400, color: '#111' }}>{u.name}</span>
                      {' '}
                      <span style={{ color: '#E65100', fontWeight: 500 }}>({u.office})</span>
                      {' — '}{u.zeroCostMonths}/{u.employedMonths} months at ₹0
                      <span style={{ color: '#999', marginLeft: 8, fontSize: 10 }}>{u.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </BrioAnalyticsSurface>
        )}

        {/* ── Flagged project staff with incomplete timesheets — collapsible drawer ── */}
        {overheadData && overheadData.flaggedUsers.length > 0 && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 8, borderLeft: '3px solid #A66257' }}>
            <div
              onClick={() => setShowIncompleteTimesheets(prev => !prev)}
              style={{ fontSize: 11, fontWeight: 400, color: '#A66257', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}
            >
              <span style={{ fontSize: 8, transition: 'transform 0.15s', transform: showIncompleteTimesheets ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              Incomplete Timesheets — Project Staff ({overheadData.flaggedUsers.length})
            </div>
            {showIncompleteTimesheets && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
                  Project staff listed below have logged significantly fewer hours than their contracted hours.
                  Their unallocated salary cost cannot be attributed to any project, which understates project costs
                  and reduces overall cost recovery. Filling in their timesheets will resolve this.
                </div>
                {(overheadData.usesLegacyHolidays || overheadData.usesLegacyLeave) && (
                  <div style={{ fontSize: 10, fontWeight: 300, color: 'var(--brio-text-secondary)', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, padding: '6px 8px', marginBottom: 8, lineHeight: 1.6 }}>
                    <span style={{ fontWeight: 500, color: '#555' }}>Contracted hours methodology</span>
                    {overheadData.usesLegacyHolidays && (
                      <span> · Public holidays estimated at 11 days/yr (NL) and 12 days/yr (IN) for periods before January 2026, when actual holiday records became available.</span>
                    )}
                    {overheadData.usesLegacyLeave && (
                      <span> · Annual leave estimated at 30 days/yr (NL) and 15 days/yr (IN) per company policy for periods before April 2026, when the time-off request system was introduced.</span>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {overheadData.flaggedUsers.map(u => (
                    <div key={u.userId} style={{ fontSize: 11, fontWeight: 300, color: 'var(--brio-text-secondary)', background: 'rgba(166, 98, 87, 0.06)', padding: '4px 8px', borderRadius: 4 }}>
                      <span style={{ fontWeight: 400, color: '#111' }}>{u.name}</span>
                      {' '}({u.role}) — {Math.round(u.loggedHours)}h / {Math.round(u.contractedHours)}h
                      {' '}· {u.gapPct}% gap · {fmtCurr(u.untrackedCost)} unallocated
                    </div>
                  ))}
                </div>
              </div>
            )}
          </BrioAnalyticsSurface>
        )}

        {/* ── Project list ── */}
        <div style={{ marginTop: 12 }}>
          <BrioByCode code={code}>
            {sortedProjects.length === 0 ? (
              <BrioAnalyticsSurface padding="m" style={{ textAlign: 'center' }}>
                <p className="brio-analytics-body" style={{ color: 'var(--brio-text-secondary)' }}>
                  No projects match the selected filter.
                </p>
              </BrioAnalyticsSurface>
            ) : (
              sortedProjects.map(project => {
                const data = projectDataMap[project.id];
                const client = activeClients.find(c => String(c.id) === String(project.clientId));
                const isOpen = expandedProjects[project.id];
                const months = data.months || [];
                const isMultiMonth = months.length > 1;

                return (
                  <BrioAnalyticsSurface key={project.id} padding="none" style={{ marginBottom: 8 }}>

                    {/* ── Collapsed row ── */}
                    <div
                      onClick={() => toggleProject(project.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                        cursor: 'pointer',
                        gap: 12,
                        borderBottom: isOpen ? '1px solid var(--brio-border)' : 'none',
                      }}
                      className="hover:bg-slate-50"
                    >
                      {isOpen
                        ? <ChevronDown size={14} style={{ color: 'var(--brio-gold)' }} />
                        : <ChevronRight size={14} style={{ color: 'var(--brio-text-secondary)' }} />
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 400, fontSize: 13 }}>{project.projectCode || project.name}</div>
                        <div style={{ fontWeight: 200, fontStyle: 'italic', color: '#666', fontSize: 11 }}>
                          {client?.organisation || client?.name || ''}
                          {isMultiMonth && periodType !== 'total' && (
                            <span style={{ marginLeft: 8, fontStyle: 'normal', color: 'var(--brio-text-secondary)' }}>
                              · {months.length} months
                            </span>
                          )}
                          {periodType === 'total' && months.length > 0 && (
                            <span style={{ marginLeft: 8, fontStyle: 'normal', color: 'var(--brio-text-secondary)' }}>
                              · {shortMonth(months[0])} – {shortMonth(months[months.length - 1])}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Summary numbers */}
                      {(() => {
                        const pid = toNumericId(project.id);
                        const projOpOH = activityOverheadEngine.totalByProject[pid] || 0;
                        const projDor = activityOverheadEngine.dormancyTotalByProject[pid] || 0;
                        const projIntOHMap = internalCostByProjectMonth[pid] || {};
                        const projIntOH = months.reduce(
                          (s, ymKey) => s + (projIntOHMap[ymKey] || 0), 0);
                        const projTotalOH = projOpOH + projDor + projIntOH;
                        return (
                      <div style={{ display: 'flex', gap: 20, alignItems: 'baseline', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Labour</div>
                          <div style={{ fontWeight: 300 }}>{fmtCurr(data.totals.labour)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Expenses</div>
                          <div style={{ fontWeight: 300 }}>{fmtCurr(data.totals.actualExp)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: '#7c6f1a', fontWeight: 300 }}>Op. Overhead</div>
                          <div style={{ fontWeight: 300, color: '#7c6f1a' }}>{fmtCurr(projOpOH)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: '#8b6914', fontWeight: 300 }}>Dormancy</div>
                          <div style={{ fontWeight: 300, color: '#8b6914' }}>{fmtCurr(projDor)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Internal OH</div>
                          <div style={{ fontWeight: 300 }}>{fmtCurr(projIntOH)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Direct Cost</div>
                          <div style={{ fontWeight: 300 }}>{fmtCurr(data.totals.totalActual)}</div>
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 80 }}>
                          <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Loaded Cost</div>
                          <div style={{ fontWeight: 400, color: 'var(--brio-gold-hover)' }}>{fmtCurr(data.totals.totalActual + projTotalOH)}</div>
                        </div>
                      </div>
                        );
                      })()}
                    </div>

                    {/* ── Expanded drawer ── */}
                    {isOpen && (() => {
                      // DEV: Log comprehensive allocation diagnostics for this project
                      if (process.env.NODE_ENV !== 'production' && data.userRows.length > 0) {
                        data.userRows.forEach(ur => {
                          const user = activeUsers.find(u => String(u.id) === String(ur.userId));
                          ur.months.forEach(md => {
                            console.log(`[ProjectCostLedger] ${ur.userName} | ${md.month}`, {
                              userId: ur.userId,
                              auth_user_id_resolved: md.authUserIdResolved ?? '(unknown)',
                              auth_user_id: user ? (resolveAuthId(user) || '(none)') : '(no user)',
                              ledger_row_found: md.ledgerRowFound ?? false,
                              source: md.source,
                              monthly_salary: Math.round(md.baseSalary || 0),
                              monthly_bonus: Math.round(md.bonus || 0),
                              markupFactor: md.markup,
                              totalMonthlyCost: Math.round(md.monthlyCost),
                              project_hours: +md.projectHours.toFixed(1),
                              total_logged: +md.totalLogged.toFixed(1),
                              contracted: md.contractedHours,
                              normalized_total: +md.normalizedTotal.toFixed(1),
                              percentWorked: +(md.pctWorked * 100).toFixed(1) + '%',
                              allocatedCost: Math.round(md.allocatedCost),
                            });
                          });
                        });
                      }
                      return null;
                    })()}
                    {isOpen && (
                      <div style={{ padding: '12px 14px' }}>

                        {/* ═════ ROW 1: SALARIES (LABOUR COST) ═════ */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--brio-text-secondary)', marginBottom: 6 }}>
                            Salaries (Labour Cost)
                          </div>

                          {data.userRows.length === 0 ? (
                            <div style={{ fontSize: 12, color: 'var(--brio-text-secondary)', fontStyle: 'italic', padding: 8 }}>
                              No time entries recorded for this project in the selected period.
                            </div>
                          ) : (
                            <BrioTable {...tableProps}>
                              <thead>
                                <tr>
                                  <th style={{ fontWeight: 400, fontSize: 11 }}>Team Member</th>
                                  <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>Project Hours</th>
                                  <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>Total Logged</th>
                                  <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>Contracted</th>
                                  <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>% Worked</th>
                                  <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>Allocated Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.userRows.map(row => (
                                  <tr key={row.userId} style={{ borderBottom: '1px solid var(--brio-border)' }}>
                                    <td style={{ fontSize: 12, fontWeight: 300 }}>
                                      {row.userName}
                                      {row.primarySource === 'missing' && (
                                        <span title="No salary data found — check ledger, salary matrix, and role history"
                                          style={{ marginLeft: 4, fontSize: 9, color: '#E65100', fontWeight: 600 }}>NO SALARY</span>
                                      )}
                                      {row.primarySource !== 'missing' && (
                                        <span style={{ marginLeft: 4, fontSize: 9, color: '#999', fontWeight: 400 }}>{row.primarySource}</span>
                                      )}
                                    </td>
                                    <td className="text-right" style={{ fontSize: 12, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>
                                      {Math.round(row.totals.projectHours)}h
                                    </td>
                                    <td className="text-right" style={{ fontSize: 12, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>
                                      {Math.round(row.totals.totalLogged)}h
                                    </td>
                                    <td className="text-right" style={{ fontSize: 12, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>
                                      {Math.round(row.totals.avgContractedHours)}h
                                      {isMultiMonth && <span style={{ fontSize: 10, color: '#999' }}> avg</span>}
                                    </td>
                                    <td className="text-right" style={{ fontSize: 12, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>
                                      {(() => { const pct = row.totals.pctWorked * 100; return pct < 10 ? pct.toFixed(1) : Math.round(pct); })()}%
                                    </td>
                                    <td className="text-right" style={{ fontSize: 12, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>
                                      {fmtCurr(row.totals.allocatedCost)}
                                    </td>
                                  </tr>
                                ))}

                                {/* Labour subtotal */}
                                <tr style={{ borderTop: '2px solid var(--brio-border)' }}>
                                  <td style={{ fontSize: 12, fontWeight: 400 }}>Total Labour</td>
                                  <td className="text-right" style={{ fontSize: 12, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>
                                    {Math.round(data.userRows.reduce((s, r) => s + r.totals.projectHours, 0))}h
                                  </td>
                                  <td colSpan={3}></td>
                                  <td className="text-right" style={{ fontSize: 12, fontWeight: 500, fontVariantNumeric: 'tabular-nums', color: 'var(--brio-gold-hover)' }}>
                                    {fmtCurr(data.totals.labour)}
                                  </td>
                                </tr>
                              </tbody>
                            </BrioTable>
                          )}
                        </div>

                        {/* ═════ ROW 2: PROJECT-SPECIFIC EXPENSES ═════ */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--brio-text-secondary)', marginBottom: 6 }}>
                            Project Expenses
                          </div>

                          <BrioTable {...tableProps}>
                            <thead>
                              <tr>
                                <th style={{ fontWeight: 400, fontSize: 11 }}>Category</th>
                                <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>Actual</th>
                                <th className="text-right" style={{ fontWeight: 400, fontSize: 11 }}>Smoothed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {EXPENSE_CATEGORIES.map(cat => (
                                <tr key={cat} style={{ borderBottom: '1px solid var(--brio-border)' }}>
                                  <td style={{ fontSize: 12, fontWeight: 300 }}>{cat}</td>
                                  <td className="text-right" style={{ fontSize: 12, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>
                                    {data.expenses.actual[cat] > 0 ? fmtCurr(data.expenses.actual[cat]) : '–'}
                                  </td>
                                  <td className="text-right" style={{ fontSize: 12, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}>
                                    {data.expenses.smoothed[cat] > 0 ? fmtCurr(data.expenses.smoothed[cat]) : '–'}
                                  </td>
                                </tr>
                              ))}
                              <tr style={{ borderTop: '2px solid var(--brio-border)' }}>
                                <td style={{ fontSize: 12, fontWeight: 400 }}>Total Expenses</td>
                                <td className="text-right" style={{ fontSize: 12, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                                  {fmtCurr(data.totals.actualExp)}
                                </td>
                                <td className="text-right" style={{ fontSize: 12, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                                  {fmtCurr(data.totals.smoothedExp)}
                                </td>
                              </tr>
                            </tbody>
                          </BrioTable>
                        </div>

                        {/* ═════ ROW 3: OVERHEAD ALLOCATION (activity-based + dormancy) ═════ */}
                        {(() => {
                          const pid = toNumericId(project.id);
                          const projOpOH = activityOverheadEngine.totalByProject[pid] || 0;
                          const projDor = activityOverheadEngine.dormancyTotalByProject[pid] || 0;
                          if (projOpOH <= 0 && projDor <= 0) return null;
                          return (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#7c6f1a', marginBottom: 6 }}>
                              Overhead Allocation
                            </div>
                            <div style={{ display: 'flex', gap: 24, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                              {projOpOH > 0 && (
                                <div>
                                  <span style={{ fontWeight: 300, color: 'var(--brio-text-secondary)' }}>Operational: </span>
                                  <span style={{ fontWeight: 400 }}>{fmtCurr(projOpOH)}</span>
                                </div>
                              )}
                              {projDor > 0 && (
                                <div>
                                  <span style={{ fontWeight: 300, color: 'var(--brio-text-secondary)' }}>Dormancy: </span>
                                  <span style={{ fontWeight: 400, color: '#8b6914' }}>{fmtCurr(projDor)}</span>
                                </div>
                              )}
                              <div style={{ fontWeight: 400, color: '#7c6f1a' }}>
                                Total: {fmtCurr(projOpOH + projDor)}
                              </div>
                            </div>
                          </div>
                          );
                        })()}

                        {/* ═════ ROW 4: TOTAL PROJECT COST (highlighted) ═════ */}
                        {(() => {
                          const pid = toNumericId(project.id);
                          const projOpOH = activityOverheadEngine.totalByProject[pid] || 0;
                          const projDor = activityOverheadEngine.dormancyTotalByProject[pid] || 0;
                          const projIntOHMap = internalCostByProjectMonth[pid] || {};
                          const projIntOH = months.reduce(
                            (s, ymKey) => s + (projIntOHMap[ymKey] || 0), 0);
                          const projTotalOH = projOpOH + projDor + projIntOH;
                          const loadedCostActual = data.totals.totalActual + projTotalOH;
                          return (
                        <div style={{
                          background: 'linear-gradient(135deg, #f8f5ee 0%, #f0ead6 100%)',
                          borderRadius: 6,
                          padding: '10px 14px',
                          marginBottom: 12,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Total Project Cost
                            </div>
                            <div style={{ display: 'flex', gap: 20, alignItems: 'baseline', fontVariantNumeric: 'tabular-nums' }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Labour</div>
                                <div style={{ fontSize: 13, fontWeight: 400 }}>{fmtCurr(data.totals.labour)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Exp (Actual)</div>
                                <div style={{ fontSize: 13, fontWeight: 400 }}>{fmtCurr(data.totals.actualExp)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Exp (Smoothed)</div>
                                <div style={{ fontSize: 13, fontWeight: 400 }}>{fmtCurr(data.totals.smoothedExp)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: '#7c6f1a', fontWeight: 300 }}>Op. Overhead</div>
                                <div style={{ fontSize: 13, fontWeight: 400, color: '#7c6f1a' }}>{fmtCurr(projOpOH)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: '#8b6914', fontWeight: 300 }}>Dormancy</div>
                                <div style={{ fontSize: 13, fontWeight: 400, color: '#8b6914' }}>{fmtCurr(projDor)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Internal OH</div>
                                <div style={{ fontSize: 13, fontWeight: 400 }}>{fmtCurr(projIntOH)}</div>
                              </div>
                              <div style={{ textAlign: 'right', borderLeft: '1px solid #d4c9a8', paddingLeft: 16 }}>
                                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Direct Cost</div>
                                <div style={{ fontSize: 13, fontWeight: 400 }}>{fmtCurr(data.totals.totalActual)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>Loaded Cost</div>
                                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--brio-gold-hover)' }}>{fmtCurr(loadedCostActual)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                          );
                        })()}

                        {/* ═════ VARIANCE DIAGNOSTICS (collapsible) ═════ */}
                        {data.userRows.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleVariance(project.id); }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 11,
                                color: 'var(--brio-text-secondary)',
                                padding: '4px 0',
                                fontFamily: 'inherit',
                              }}
                            >
                              <Info size={12} />
                              {expandedVariance[project.id] ? 'Hide' : 'Show'} Variance Diagnostics
                            </button>

                            {expandedVariance[project.id] && (
                              <div style={{ marginTop: 6, background: '#fafaf5', borderRadius: 4, padding: 10, border: '1px solid #eee' }}>
                                <div style={{ fontSize: 10, color: '#999', marginBottom: 6 }}>
                                  Variance diagnostics — for audit/verification only. Normalization (max(logged, contracted)) is applied to allocation math above.
                                </div>
                                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                                      <th style={{ textAlign: 'left', fontWeight: 400, padding: '3px 6px' }}>User</th>
                                      <th style={{ textAlign: 'left', fontWeight: 400, padding: '3px 6px' }}>Month</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Recorded</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Contracted</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Theo. Hourly</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Fixed Monthly</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Variance</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Tolerance</th>
                                      <th style={{ textAlign: 'right', fontWeight: 400, padding: '3px 6px' }}>Excess</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {data.userRows.flatMap(ur =>
                                      ur.varianceDiag.map((vd, idx) => (
                                        <tr key={`${ur.userId}-${vd.month}`} style={{ borderBottom: '1px solid #eee' }}>
                                          {idx === 0 ? (
                                            <td rowSpan={ur.varianceDiag.length} style={{ padding: '3px 6px', fontWeight: 300, verticalAlign: 'top' }}>
                                              {ur.userName}
                                            </td>
                                          ) : null}
                                          <td style={{ padding: '3px 6px', fontWeight: 200 }}>{shortMonth(vd.month)}</td>
                                          <td style={{ textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums' }}>{vd.recordedHours.toFixed(1)}h</td>
                                          <td style={{ textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums' }}>{vd.contractedHours}h</td>
                                          <td style={{ textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(vd.theoreticalHourlyCost)}</td>
                                          <td style={{ textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(vd.fixedMonthlySalaryCost)}</td>
                                          <td style={{
                                            textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums',
                                            color: vd.variance > 0 ? '#7F7321' : vd.variance < 0 ? '#b71c1c' : 'inherit',
                                          }}>
                                            {vd.variance > 0 ? '+' : ''}{fmtCurr(vd.variance)}
                                          </td>
                                          <td style={{ textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurr(vd.toleranceBand)}</td>
                                          <td style={{
                                            textAlign: 'right', padding: '3px 6px', fontVariantNumeric: 'tabular-nums',
                                            color: vd.excessVariance > 0 ? '#E65100' : 'inherit',
                                            fontWeight: vd.excessVariance > 0 ? 400 : 200,
                                          }}>
                                            {vd.excessVariance > 0 ? fmtCurr(vd.excessVariance) : '–'}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    )}
                  </BrioAnalyticsSurface>
                );
              })
            )}
          </BrioByCode>
        </div>

        </div>
      </BrioFrame>
    </div>
  );
}
